import { useState } from "react";
import {
  Box,
  TextField,
  Typography,
  Button,
  InputAdornment,
  IconButton,
  Grid,
  Paper,
  Divider,
  CircularProgress,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Checkbox,
} from "@mui/material";
import { Link, useNavigate } from "react-router-dom";
import {
  Visibility,
  VisibilityOff,
  Person,
  Email,
  Lock,
  Google,
  Business,
} from "@mui/icons-material";
import { useDispatch, useSelector } from "react-redux";
import { login, setTwoFaUsername } from "../../store/slice/AuthSlice";
import backgroundImage from "../../assets/backgroundImage.jpg";
import squareLogo from "../../assets/squareLogo.png";
import muzukuru from "../../assets/muzukuru.png";
import { motion } from "framer-motion";
import apiService from "../../service/Api";
import { easeInOut } from "framer-motion";
import { showErrorToast } from "../../components/common/ShowToast";
import { RootState } from "../../store/Index";
import TermsModal from "../../components/TermsModal";

// Define User interface to match backend response
interface User {
  id: number;
  username: string;
  email: string;
  user_type: string;
}

interface SignupResponse {
  user: User;
  is_2fa_enabled: boolean;
  message: string;
}

const Signup = () => {
  const reduxTheme = useSelector((state: RootState) => state.theme);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [username, setUsername] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [userType, setUserType] = useState("individual");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [focusedField, setFocusedField] = useState<string | null>(null);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [openTermsModal, setOpenTermsModal] = useState(false);
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Client-side validation
    if (!username || !email || !password || !confirmPassword || !userType) {
      showErrorToast("Please fill in all required fields.");
      return;
    }
    if (password !== confirmPassword) {
      showErrorToast("Passwords do not match.");
      return;
    }
    if (!/\S+@\S+\.\S+/.test(email)) {
      showErrorToast("Please enter a valid email address.");
      return;
    }
    if (password.length < 8) {
      showErrorToast("Password must be at least 8 characters long.");
      return;
    }
    if (userType === "individual" && (!firstName || !lastName)) {
      showErrorToast(
        "First name and last name are required for individual users."
      );
      return;
    }
    if (userType === "company_user" && !companyName) {
      showErrorToast("Company name is required for company users.");
      return;
    }
    if (!termsAccepted) {
      showErrorToast("You must agree to the Terms and Conditions.");
      return;
    }

    setIsLoading(true);
    try {
      const payload = {
        username,
        email,
        password,
        user_type: userType,
        ...(userType === "individual" && {
          first_name: firstName,
          last_name: lastName,
        }),
        ...(userType === "company_user" && {
          company_name: companyName,
        }),
      };

      const response = await apiService.post<SignupResponse>(
        "/api/authentication/signup/",
        payload,
        true
      );

      // Store username for 2FA setup in Redux
      dispatch(setTwoFaUsername(response.data.user.username));
      navigate("/setup-2fa");
    } catch (err: any) {
      console.error("Signup error:", {
        message: err.message,
        response: err.response?.data,
        status: err.response?.status,
      });
      const errorMsg =
        err.response?.data?.message ||
        err.message ||
        "An error occurred. Please try again.";
      showErrorToast(errorMsg);
    } finally {
      setIsLoading(false);
    }
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        duration: 0.6,
        staggerChildren: 0.08,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.5 },
    },
  };

  const formFields = [
    {
      label: "Username",
      value: username,
      setValue: setUsername,
      type: "text",
      placeholder: "Enter your username",
      icon: Person,
      fieldKey: "username",
    },
    {
      label: "Email",
      value: email,
      setValue: setEmail,
      type: "email",
      placeholder: "Enter your email address",
      icon: Email,
      fieldKey: "email",
    },
    {
      label: "Password",
      value: password,
      setValue: setPassword,
      type: showPassword ? "text" : "password",
      placeholder: "Create a password",
      icon: Lock,
      toggle: () => setShowPassword(!showPassword),
      show: showPassword,
      fieldKey: "password",
    },
    {
      label: "Confirm Password",
      value: confirmPassword,
      setValue: setConfirmPassword,
      type: showConfirmPassword ? "text" : "password",
      placeholder: "Confirm your password",
      icon: Lock,
      toggle: () => setShowConfirmPassword(!showConfirmPassword),
      show: showConfirmPassword,
      fieldKey: "confirmPassword",
    },
    ...(userType === "individual"
      ? [
          {
            label: "First Name",
            value: firstName,
            setValue: setFirstName,
            type: "text",
            placeholder: "Enter your first name",
            icon: Person,
            fieldKey: "firstName",
          },
          {
            label: "Last Name",
            value: lastName,
            setValue: setLastName,
            type: "text",
            placeholder: "Enter your last name",
            icon: Person,
            fieldKey: "lastName",
          },
        ]
      : [
          {
            label: "Company Name",
            value: companyName,
            setValue: setCompanyName,
            type: "text",
            placeholder: "Enter your company name",
            icon: Business,
            fieldKey: "companyName",
          },
        ]),
  ];

  const renderFormField = (field: any, index: number) => (
    <motion.div key={index} variants={itemVariants}>
      <Box sx={{ mb: 3 }}>
        <Typography
          variant="body2"
          sx={{
            mb: 2,
            fontWeight: 600,
            color: reduxTheme.neutral_colors?.[100] || "#333",
            fontSize: "14px",
            textTransform: "uppercase",
            letterSpacing: "0.5px",
          }}
        >
          {field.label}
        </Typography>
        <TextField
          placeholder={field.placeholder}
          variant="outlined"
          fullWidth
          type={field.type}
          value={field.value}
          onChange={(e) => field.setValue(e.target.value)}
          onFocus={() => setFocusedField(field.fieldKey)}
          onBlur={() => setFocusedField(null)}
          disabled={isLoading}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <field.icon
                  sx={{
                    color:
                      focusedField === field.fieldKey
                        ? reduxTheme.primary_colors?.[400] || "#614438"
                        : reduxTheme.neutral_colors?.[400] || "#999",
                    transition: "color 0.3s",
                  }}
                />
              </InputAdornment>
            ),
            ...(field.toggle && {
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={field.toggle}
                    edge="end"
                    disabled={isLoading}
                    sx={{
                      color: field.show
                        ? reduxTheme.primary_colors?.[400] || "#614438"
                        : reduxTheme.neutral_colors?.[400] || "#999",
                      "&:hover": {
                        backgroundColor: `rgba(97, 68, 56, 0.04)`,
                      },
                    }}
                  >
                    {field.show ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }),
          }}
          sx={{
            "& .MuiOutlinedInput-root": {
              borderRadius: "12px",
              backgroundColor: "rgba(255, 255, 255, 0.8)",
              transition: "all 0.3s ease",
              height: "56px",
              "& fieldset": {
                borderColor:
                  reduxTheme.neutral_colors?.[300] || "rgba(0,0,0,0.1)",
                borderWidth: "2px",
              },
              "&:hover fieldset": {
                borderColor:
                  reduxTheme.neutral_colors?.[400] || "rgba(194, 147, 99, 0.5)",
              },
              "&.Mui-focused": {
                backgroundColor: "rgba(255, 255, 255, 1)",
                transform: "translateY(-2px)",
                boxShadow: `0 8px 25px rgba(${
                  reduxTheme.primary_colors?.[400]
                    ? parseInt(reduxTheme.primary_colors[400].slice(1, 3), 16)
                    : 97
                }, ${
                  reduxTheme.primary_colors?.[400]
                    ? parseInt(reduxTheme.primary_colors[400].slice(3, 5), 16)
                    : 68
                }, ${
                  reduxTheme.primary_colors?.[400]
                    ? parseInt(reduxTheme.primary_colors[400].slice(5, 7), 16)
                    : 56
                }, 0.15)`,
                "& fieldset": {
                  borderColor: reduxTheme.primary_colors?.[400] || "#614438",
                },
              },
            },
            "& .MuiInputBase-input": {
              color: reduxTheme.neutral_colors?.[100] || "#333",
              fontSize: "16px",
            },
          }}
        />
      </Box>
    </motion.div>
  );

  return (
    <Grid container sx={{ minHeight: "100vh", overflow: "hidden" }}>
      {/* Left Column - Enhanced Image Background */}
      <Grid
        size={{ xs: false, md: 6 }}
        sx={{
          position: "relative",
          display: { xs: "none", md: "block" },
          background: `linear-gradient(135deg, ${
            reduxTheme.primary_colors?.[500] || "#614438"
          }80 0%, ${
            reduxTheme.primary_colors?.[400] || "#C29363"
          }99 100%), url(${backgroundImage})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          "&::before": {
            content: '""',
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background:
              "linear-gradient(45deg, rgba(97, 68, 56, 0.1) 0%, transparent 50%)",
            zIndex: 1,
          },
        }}
      >
        {/* Floating geometric shapes */}
        <Box
          sx={{
            position: "absolute",
            top: "15%",
            right: "10%",
            width: "80px",
            height: "80px",
            borderRadius: "50%",
            background: "rgba(194, 147, 99, 0.2)",
            zIndex: 2,
          }}
          component={motion.div}
          animate={{
            y: [0, -15, 0],
            rotate: [0, 90, 180],
          }}
          transition={{
            duration: 10,
            repeat: Infinity,
            ease: easeInOut,
          }}
        />
        <Box
          sx={{
            position: "absolute",
            top: "70%",
            right: "20%",
            width: "50px",
            height: "50px",
            borderRadius: "8px",
            background: "rgba(255, 255, 255, 0.1)",
            backdropFilter: "blur(10px)",
            zIndex: 2,
          }}
          component={motion.div}
          animate={{
            x: [0, 20, 0],
            rotate: [0, -90, -180],
          }}
          transition={{
            duration: 7,
            repeat: Infinity,
            ease: easeInOut,
          }}
        />
        {/* Enhanced Logo Section */}
        <Box
          sx={{
            position: "absolute",
            bottom: 40,
            left: 40,
            display: "flex",
            alignItems: "center",
            zIndex: 5,
          }}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            whileHover={{ scale: 1.05 }}
          >
            <Paper
              elevation={0}
              sx={{
                p: 2,
                borderRadius: "16px",
                background: "rgba(255, 255, 255, 0.1)",
                backdropFilter: "blur(20px)",
                border: "1px solid rgba(255, 255, 255, 0.2)",
                display: "flex",
                alignItems: "center",
                gap: 2,
              }}
            >
              <img
                src={squareLogo}
                width="110px"
                height="65px"
                alt="Square Logo"
                style={{ cursor: "pointer" }}
              />
              <img
                src={muzukuru}
                width="165px"
                height="70px"
                alt="Muzukuru Logo"
                style={{ cursor: "pointer" }}
              />
            </Paper>
          </motion.div>
        </Box>
        {/* Welcome text overlay */}
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            textAlign: "center",
            zIndex: 3,
            color: "white",
          }}
        >
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.5 }}
          >
            <Typography
              variant="h3"
              sx={{
                fontWeight: 700,
                mb: 2,
                textShadow: "0 4px 20px rgba(0,0,0,0.3)",
                background:
                  "linear-gradient(45deg, #fff 30%, rgba(194, 147, 99, 1) 90%)",
                backgroundClip: "text",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
              }}
            >
              Join Us Today
            </Typography>
            <Typography
              variant="h6"
              sx={{
                opacity: 0.9,
                textShadow: "0 2px 10px rgba(0,0,0,0.3)",
                fontWeight: 300,
              }}
            >
              Create your account and start your journey
            </Typography>
          </motion.div>
        </Box>
      </Grid>
      {/* Right Column - Enhanced Signup Form */}
      <Grid
        size={{ xs: 12, md: 6 }}
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          px: { xs: 2, sm: 4, md: 8 },
          py: { xs: 4, md: 0 },
          background: `linear-gradient(135deg, ${
            reduxTheme.neutral_colors?.[900] || "#fafafa"
          } 0%, ${reduxTheme.neutral_colors?.[800] || "#f5f5f5"} 100%)`,
          position: "relative",
          "&::before": {
            content: '""',
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background:
              "radial-gradient(circle at 20% 80%, rgba(194, 147, 99, 0.05) 0%, transparent 50%)",
            zIndex: 1,
          },
        }}
      >
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          style={{ width: "100%", maxWidth: "600px", zIndex: 2 }}
        >
          <Paper
            elevation={0}
            sx={{
              width: "100%",
              background: "rgba(255, 255, 255, 0.9)",
              backdropFilter: "blur(20px)",
              padding: "48px",
              borderRadius: "24px",
              border: "1px solid rgba(255, 255, 255, 0.2)",
              boxShadow: "0 20px 60px rgba(0, 0, 0, 0.1)",
              position: "relative",
              overflow: "hidden",
              "&::before": {
                content: '""',
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                height: "4px",
                background: `linear-gradient(90deg, ${
                  reduxTheme.primary_colors?.[400] || "#614438"
                } 0%, ${reduxTheme.primary_colors?.[300] || "#C29363"} 100%)`,
              },
            }}
            component={motion.div}
            whileHover={{ y: -5 }}
            transition={{ duration: 0.3 }}
          >
            <Box component="form" onSubmit={handleSubmit}>
              <motion.div variants={itemVariants}>
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 700,
                    background: `linear-gradient(135deg, ${
                      reduxTheme.primary_colors?.[400] || "#614438"
                    } 0%, ${
                      reduxTheme.primary_colors?.[300] || "#C29363"
                    } 100%)`,
                    backgroundClip: "text",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                    textAlign: "center",
                    marginBottom: "8px",
                    letterSpacing: "-0.02em",
                  }}
                >
                  Sign Up
                </Typography>
              </motion.div>
              <motion.div variants={itemVariants}>
                <Typography
                  variant="body1"
                  sx={{
                    color: reduxTheme.neutral_colors?.[500] || "#666",
                    textAlign: "center",
                    marginBottom: "40px",
                    fontSize: "16px",
                    lineHeight: 1.6,
                  }}
                >
                  Create your account to get started with amazing features.
                </Typography>
              </motion.div>
              {/* User Type Selection */}
              <motion.div variants={itemVariants}>
                <Box sx={{ mb: 3 }}>
                  <Typography
                    variant="body2"
                    sx={{
                      mb: 2,
                      fontWeight: 600,
                      color: reduxTheme.neutral_colors?.[100] || "#333",
                      fontSize: "14px",
                      textTransform: "uppercase",
                      letterSpacing: "0.5px",
                    }}
                  >
                    Account Type
                  </Typography>
                  <FormControl component="fieldset">
                    <RadioGroup
                      row
                      value={userType}
                      onChange={(e) => setUserType(e.target.value)}
                      sx={{ justifyContent: "center" }}
                    >
                      <FormControlLabel
                        value="individual"
                        control={
                          <Radio
                            sx={{
                              color: reduxTheme.neutral_colors?.[400] || "#999",
                              "&.Mui-checked": {
                                color:
                                  reduxTheme.primary_colors?.[400] || "#614438",
                              },
                            }}
                          />
                        }
                        label="Individual"
                        sx={{ mr: 4 }}
                      />
                      <FormControlLabel
                        value="company_user"
                        control={
                          <Radio
                            sx={{
                              color: reduxTheme.neutral_colors?.[400] || "#999",
                              "&.Mui-checked": {
                                color:
                                  reduxTheme.primary_colors?.[400] || "#614438",
                              },
                            }}
                          />
                        }
                        label="Company"
                      />
                    </RadioGroup>
                  </FormControl>
                </Box>
              </motion.div>

              {/* Form Fields in Two Columns */}
              <Grid container spacing={3}>
                {formFields.map((field, index) => (
                  <Grid size={{ xs: 12, sm: 6 }} key={index}>
                    {renderFormField(field, index)}
                  </Grid>
                ))}
              </Grid>

              {/* Terms and Conditions Checkbox */}
              <motion.div variants={itemVariants}>
                <Box sx={{ mb: 3, display: "flex", alignItems: "center" }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={termsAccepted}
                        onChange={(e) => setTermsAccepted(e.target.checked)}
                        sx={{
                          color: reduxTheme.neutral_colors?.[400] || "#999",
                          "&.Mui-checked": {
                            color:
                              reduxTheme.primary_colors?.[400] || "#614438",
                          },
                        }}
                        disabled={isLoading}
                      />
                    }
                    label={
                      <Typography
                        variant="body2"
                        sx={{
                          color: reduxTheme.neutral_colors?.[100] || "#333",
                          fontSize: "14px",
                        }}
                      >
                        I agree to the{" "}
                        <span
                          style={{
                            color:
                              reduxTheme.primary_colors?.[400] || "#614438",
                            textDecoration: "underline",
                            cursor: "pointer",
                          }}
                          onClick={() => setOpenTermsModal(true)}
                        >
                          Terms and Conditions
                        </span>
                      </Typography>
                    }
                  />
                </Box>
              </motion.div>

              <motion.div variants={itemVariants}>
                <Button
                  fullWidth
                  type="submit"
                  variant="contained"
                  disabled={isLoading}
                  component={motion.button}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  sx={{
                    background: `linear-gradient(135deg, ${
                      reduxTheme.primary_colors?.[400] || "#614438"
                    } 0%, ${
                      reduxTheme.primary_colors?.[300] || "#C29363"
                    } 100%)`,
                    textTransform: "none",
                    padding: "16px",
                    borderRadius: "12px",
                    mb: 3,
                    fontSize: "16px",
                    fontWeight: 600,
                    boxShadow: `0 8px 25px rgba(${
                      reduxTheme.primary_colors?.[400]
                        ? parseInt(
                            reduxTheme.primary_colors[400].slice(1, 3),
                            16
                          )
                        : 97
                    }, ${
                      reduxTheme.primary_colors?.[400]
                        ? parseInt(
                            reduxTheme.primary_colors[400].slice(3, 5),
                            16
                          )
                        : 68
                    }, ${
                      reduxTheme.primary_colors?.[400]
                        ? parseInt(
                            reduxTheme.primary_colors[400].slice(5, 7),
                            16
                          )
                        : 56
                    }, 0.3)`,
                    border: "none",
                    "&:hover": {
                      background: `linear-gradient(135deg, ${
                        reduxTheme.primary_colors?.[500] || "#503629"
                      } 0%, ${
                        reduxTheme.primary_colors?.[400] || "#A67C52"
                      } 100%)`,
                      boxShadow: `0 12px 35px rgba(${
                        reduxTheme.primary_colors?.[400]
                          ? parseInt(
                              reduxTheme.primary_colors[400].slice(1, 3),
                              16
                            )
                          : 97
                      }, ${
                        reduxTheme.primary_colors?.[400]
                          ? parseInt(
                              reduxTheme.primary_colors[400].slice(3, 5),
                              16
                            )
                          : 68
                      }, ${
                        reduxTheme.primary_colors?.[400]
                          ? parseInt(
                              reduxTheme.primary_colors[400].slice(5, 7),
                              16
                            )
                          : 56
                      }, 0.4)`,
                    },
                    "&:disabled": {
                      background: `rgba(${
                        reduxTheme.primary_colors?.[400]
                          ? parseInt(
                              reduxTheme.primary_colors[400].slice(1, 3),
                              16
                            )
                          : 97
                      }, ${
                        reduxTheme.primary_colors?.[400]
                          ? parseInt(
                              reduxTheme.primary_colors[400].slice(3, 5),
                              16
                            )
                          : 68
                      }, ${
                        reduxTheme.primary_colors?.[400]
                          ? parseInt(
                              reduxTheme.primary_colors[400].slice(5, 7),
                              16
                            )
                          : 56
                      }, 0.3)`,
                    },
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  {isLoading ? (
                    <CircularProgress
                      size={24}
                      sx={{ color: reduxTheme.neutral_colors?.[100] || "#fff" }}
                    />
                  ) : (
                    "Create Account"
                  )}
                </Button>
              </motion.div>
              <motion.div variants={itemVariants}>
                <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
                  <Divider
                    sx={{
                      flex: 1,
                      borderColor:
                        reduxTheme.neutral_colors?.[300] || "rgba(0,0,0,0.1)",
                    }}
                  />
                  <Typography
                    sx={{
                      px: 2,
                      color: reduxTheme.neutral_colors?.[500] || "#999",
                      fontSize: "14px",
                      fontWeight: 500,
                    }}
                  >
                    or
                  </Typography>
                  <Divider
                    sx={{
                      flex: 1,
                      borderColor:
                        reduxTheme.neutral_colors?.[300] || "rgba(0,0,0,0.1)",
                    }}
                  />
                </Box>
              </motion.div>
              <motion.div variants={itemVariants}>
                <Button
                  fullWidth
                  variant="outlined"
                  disabled
                  component={motion.button}
                  whileHover={{ scale: 1.02 }}
                  sx={{
                    textTransform: "none",
                    padding: "16px",
                    borderRadius: "12px",
                    mb: 3,
                    borderColor:
                      reduxTheme.neutral_colors?.[300] || "rgba(0,0,0,0.1)",
                    borderWidth: "2px",
                    color: reduxTheme.neutral_colors?.[100] || "#666",
                    fontSize: "16px",
                    fontWeight: 600,
                    backgroundColor: "rgba(255, 255, 255, 0.8)",
                    "&:hover": {
                      borderColor:
                        reduxTheme.neutral_colors?.[400] ||
                        "rgba(194, 147, 99, 0.3)",
                      backgroundColor: "rgba(255, 255, 255, 1)",
                      boxShadow: "0 4px 15px rgba(0,0,0,0.05)",
                    },
                    "&:disabled": {
                      borderColor:
                        reduxTheme.neutral_colors?.[400] || "rgba(0,0,0,0.1)",
                      color: reduxTheme.neutral_colors?.[500] || "#999",
                    },
                  }}
                  startIcon={<Google sx={{ color: "#DB4437" }} />}
                >
                  Continue with Google
                </Button>
              </motion.div>
              <motion.div variants={itemVariants}>
                <Typography
                  variant="body2"
                  align="center"
                  sx={{
                    color: reduxTheme.neutral_colors?.[100] || "#666",
                    fontSize: "15px",
                  }}
                >
                  Already have an account?{" "}
                  <Link
                    to="/login"
                    style={{
                      textDecoration: "none",
                      fontWeight: 600,
                      background: `linear-gradient(135deg, ${
                        reduxTheme.primary_colors?.[300] || "#C29363"
                      } 0%, ${
                        reduxTheme.primary_colors?.[400] || "#614438"
                      } 100%)`,
                      backgroundClip: "text",
                      WebkitBackgroundClip: "text",
                      WebkitTextFillColor: "transparent",
                    }}
                  >
                    <motion.span whileHover={{ scale: 1.05 }}>
                      Sign In
                    </motion.span>
                  </Link>
                </Typography>
              </motion.div>
            </Box>
          </Paper>
        </motion.div>
      </Grid>
      {/* Terms and Conditions Modal */}
      <TermsModal
        open={openTermsModal}
        onClose={() => setOpenTermsModal(false)}
        onAccept={() => {
          setTermsAccepted(true);
          setOpenTermsModal(false);
        }}
      />
    </Grid>
  );
};

export default Signup;
